/**
 * The hero mark's tuning — every number that was arrived at by measuring
 * something, kept together and away from the markup that consumes it.
 *
 * These live here rather than inside `HeroMark` for the reason
 * `.claude/rules/architecture.md` gives for hoisting a leaf: they import
 * nothing from their parent, and the justification each one needs is longer
 * than the value itself, so inline they buried the component's structure under
 * five paragraphs of rationale. They are also constants, not per-render values.
 */

/**
 * The edge dissolve.
 *
 * The mark is much taller than the band it sits in — deliberately, since a
 * light source that fits inside its own frame reads as a shape — so its edges
 * have to be dissolved rather than cropped. Two masks, intersected by the call
 * site.
 *
 * The vertical one carries a MEASURED number rather than a tasteful one. The
 * mark is centred on the hero block, which puts its top edge 143px above the
 * viewport top and the sticky header's lower border at 20% of its height. A
 * fade that reached full strength before that (the first version did, at 18%)
 * left the mark at full opacity exactly where the header stops covering it, so
 * the band appeared to have a hard-edged coloured strip along its top. Holding
 * transparent to 16% and ramping to 44% puts the header line at ~14% opacity,
 * which reads as the light falling off toward the top of the frame — which is
 * what light does.
 *
 * The radial one only keeps the corners from squaring off, and is deliberately
 * wide: at `closest-side 62%` it stopped being a falloff and became a visible
 * disc, and a circle drawn behind a headline is exactly what this is not.
 */
export const EDGE_FADE = [
  'linear-gradient(to bottom, transparent 0%, transparent 16%, #000 44%, #000 74%, transparent 98%)',
  'radial-gradient(closest-side, #000 80%, transparent 100%)',
].join(',');

/** One pool of the aurora: where it sits, what it is, and when it lands. */
export interface AuroraPool {
  readonly background: string;
  /** The pool's own centre, so its landing scales about itself. */
  readonly origin: string;
  /** Seconds after the pen sets off. */
  readonly delay: string;
}

/**
 * The aurora — three ramp-ordered pools, cyan through violet, that carry the
 * hero's colour. Pure gradient, so there is no silhouette to be misread and it
 * can hold real saturation.
 *
 * Alpha is per-pool rather than a blanket `opacity`, so each stop keeps its own
 * hue instead of all three collapsing toward the page white at the same rate —
 * which is precisely what made the original 10% watermark read as grey.
 *
 * ## Three elements, because one uniform fade was the problem
 *
 * These were a single `background` shorthand on one div, fading in over 2.6s.
 * That is a slow global dissolve: by a third of the way through the write, the
 * entire field was already coloured, everywhere at once, while the pen was
 * still on the first blade. Whatever the pen-following bloom did behind it was
 * invisible under a wash that had already arrived.
 *
 * Split into three, each pool can LAND on its own beat — scaling up from small,
 * overshooting, and settling, about its own centre — so the colour arrives as
 * three drops hitting paper in sequence rather than as a curtain coming up. The
 * delays run left to right because the pen does.
 *
 * All three sit in the mark's UPPER half, and that is a contrast decision
 * rather than a compositional one. The violet pool was at `78% 70%`, which
 * lands exactly on the proof strip — the widest and lowest-contrast text on the
 * page — and dragged it to a measured 2.81:1 against an AA floor of 4.5. Colour
 * is cheapest where the ink is heaviest, so the pools sit across the headline
 * band, where black 64px type still measures ~10:1 over the darkest point.
 */
export const AURORA_POOLS: readonly AuroraPool[] = [
  {
    background:
      'radial-gradient(36% 32% at 22% 30%, rgb(var(--bd-cyan-rgb) / 0.26), transparent 70%)',
    origin: '22% 30%',
    delay: '.18s',
  },
  {
    background:
      'radial-gradient(42% 36% at 52% 40%, rgb(var(--bd-blue-rgb) / 0.20), transparent 72%)',
    origin: '52% 40%',
    delay: '.92s',
  },
  {
    background:
      'radial-gradient(34% 32% at 82% 46%, rgb(var(--bd-violet-rgb) / 0.23), transparent 70%)',
    origin: '82% 46%',
    delay: '1.7s',
  },
];

/**
 * A pool's landing. Short and overshooting — the two properties that separate
 * something arriving from something appearing.
 */
export const SPLAT = '.85s cubic-bezier(.18,.9,.25,1)';

/**
 * The write, and the one number that was worth slowing down.
 *
 * The design bundle runs it in 1.25s, which is right for a mark you are looking
 * AT. This one is up to 1,080px of ground behind a headline you are reading,
 * and at 1.25s the drawing front crosses that distance fast enough to register
 * as a flicker at the edge of vision — motion you notice and cannot identify, which
 * is the worst of both. At 3.2s the same gesture reads as deliberate: slow
 * enough to follow if you watch it, slow enough to ignore if you are reading.
 * `.15s` in, so it never fights the page paint.
 */
export const WRITE = '3.2s cubic-bezier(.34,.04,.16,1) .15s forwards';

/**
 * The colour's clock, one beat behind the pen's.
 *
 * The same duration and easing as `WRITE`, offset by `.34s` — so the wash
 * tracks the stroke's leading edge at a FIXED distance rather than racing it or
 * falling further behind as the stroke goes on. A different duration here is what makes the two
 * motions look unrelated, which is the defect this replaced: the colour used to
 * fade up as a whole shape on its own timer, so the mark was written by a pen
 * on one path while the colour arrived everywhere at once on another.
 */
export const WASH = '3.2s cubic-bezier(.34,.04,.16,1) .34s forwards';

/**
 * Fit the blades to their box.
 *
 * `MARK_VIEWBOX` is `0 0 1254 1254` but the two blades only occupy
 * `x 331→921, y 281→912` inside it — 47% of the width and 50% of the height,
 * measured from the live `getBBox()`. At logo sizes that generous margin is
 * correct; at hero size it means half the element is empty, so the box stops
 * being a usable size control.
 *
 * This scales the ink about its own centre so the box IS the mark. The centre
 * is not re-measured: `logo-paths.ts` already records it as the middle term of
 * `BADGE_FIT_TRANSFORM` (`-626.51 -596.63`), which is the same fit the badge
 * tile uses — so the two cannot disagree about where the mark's centre is.
 *
 * 1.66 leaves ~100 user-units of margin, which the bloom's blur needs to avoid
 * clipping at the viewBox edge — an SVG filter region is finite, and a blurred
 * fill that reaches the edge ends in a straight line, which is the one thing a
 * light source must never do.
 */
export const MARK_FIT = 'translate(627 627) scale(1.66) translate(-626.51 -596.63)';

/**
 * The wash's SPREAD — the mask stroke widening after the pen has passed.
 *
 * Deliberately longer than `WASH` itself and eased hard out of the gate: the
 * spread should still be happening after the stroke has finished being laid
 * down, because that is what ink does. Paper keeps drinking after the pen has
 * left.
 */
export const WASH_SPREAD = '2.4s cubic-bezier(.16,1,.3,1) .3s forwards';

/** The contour's hairline, in user units. Sharp at every breakpoint. */
export const CONTOUR_WIDTH = 6;

/**
 * The wash mask's stroke, in user units.
 *
 * Wide enough that a stroke straddling each blade's OUTLINE covers that blade's
 * interior — the paths are closed silhouettes, not centre lines, so a narrow
 * mask would reveal a rim and leave the middle empty.
 */
export const WASH_STROKE = 300;
